import { useEffect, useState, useCallback } from "react";
import config from "../config";
import logger from "../utils/logger";

/**
 * Custom hook to manage organization settings with caching and cross-tab sync
 *
 * Handles:
 * - Fetching organization settings from backend
 * - LocalStorage caching with 5-minute TTL
 * - Cross-tab synchronization via storage events
 * - Periodic polling for settings changes
 *
 * @param {string} organizationId - The organization ID to fetch settings for
 * @param {Function} authenticatedFetch - Authenticated fetch function
 * @param {Object} propOrgSettings - Optional settings provided via props (overrides fetching)
 * @returns {Object} Organization settings object
 */
export const useOrganizationSettings = (
  organizationId,
  authenticatedFetch,
  propOrgSettings = null
) => {
  const [localOrgSettings, setLocalOrgSettings] = useState({
    autoProvisionCredentials: true,
    receiveCredentialNotifications: true,
    enableDevStreamsForAllRoles: false,
  });

  // Use prop settings if provided, otherwise use local state
  const orgSettings = propOrgSettings || localOrgSettings;

  // Stabilize setOrgSettings to avoid re-renders when propOrgSettings changes
  const setOrgSettings = useCallback(
    (settings) => {
      if (!propOrgSettings) {
        setLocalOrgSettings(settings);
      }
      // No-op if using props
    },
    [propOrgSettings]
  );

  // Effect to fetch organization settings (only if not provided via props)
  useEffect(() => {
    // Skip fetch if settings provided via props
    if (propOrgSettings) return;

    const fetchOrgSettings = async () => {
      if (!organizationId) return;

      // Check cache first
      const cacheKey = `orgSettings_${organizationId}`;
      const cached = localStorage.getItem(cacheKey);

      if (cached) {
        try {
          const { settings, timestamp } = JSON.parse(cached);
          const age = Date.now() - timestamp;
          const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

          if (age < CACHE_TTL) {
            setOrgSettings(settings);
            logger.log("Organization settings loaded from cache");
            return;
          }
        } catch (e) {
          logger.warn("Failed to parse cached settings", e);
        }
      }

      try {
        logger.log(`Fetching organization settings for ${organizationId}`);
        const response = await authenticatedFetch(
          `${config.servicesApiEndpoint}/organizations/${encodeURIComponent(
            organizationId
          )}/settings`,
          {
            method: "GET",
            headers: { "x-api-key": config.apiKey },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setOrgSettings(data.settings);

          // Cache the settings
          localStorage.setItem(
            cacheKey,
            JSON.stringify({ settings: data.settings, timestamp: Date.now() })
          );

          logger.log("Organization settings loaded:", data.settings);
        } else {
          logger.warn("Failed to fetch organization settings, using defaults");
        }
      } catch (error) {
        logger.error("Error fetching organization settings:", error);
      }
    };

    fetchOrgSettings();
  }, [organizationId, propOrgSettings, setOrgSettings, authenticatedFetch]);

  // Effect to listen for cross-tab settings updates and periodic polling
  useEffect(() => {
    if (!organizationId) return;

    const cacheKey = `orgSettings_${organizationId}`;

    // Handle storage events from other tabs
    const handleStorageChange = (event) => {
      if (event.key === cacheKey && event.newValue) {
        try {
          const { settings } = JSON.parse(event.newValue);
          setOrgSettings(settings);
          logger.log("Organization settings updated from another tab");
        } catch (e) {
          logger.warn("Failed to parse settings from storage event", e);
        }
      }
    };

    // Periodic polling to detect changes
    const pollInterval = setInterval(() => {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        try {
          const { settings } = JSON.parse(cached);
          const currentSettingsStr = JSON.stringify(orgSettings);
          const cachedSettingsStr = JSON.stringify(settings);

          if (currentSettingsStr !== cachedSettingsStr) {
            setOrgSettings(settings);
            logger.log("Organization settings updated from polling");
          }
        } catch (e) {
          logger.warn("Failed to parse settings during polling", e);
        }
      }
    }, 60000); // Poll every 60 seconds

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(pollInterval);
    };
  }, [organizationId, orgSettings, setOrgSettings]);

  return { orgSettings, setOrgSettings };
};

export default useOrganizationSettings;
